import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getSurvey, submitSurvey } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, CheckCircle2, AlertCircle, ChevronRight, Shield } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default questions (will be fetched from API)
function getDefaultQuestions() {
  return [
    {
      id: 'q1',
      question: 'Hvad er vigtigst for dig i løsningen?',
      type: 'radio',
      options: [
        { value: 'fungerer_godt', label: 'At det fungerer godt i hverdagen' },
        { value: 'ser_flot_ud', label: 'At det ser flot ud og passer til rummet' },
        { value: 'enkel_proces', label: 'At processen er enkel og overskuelig' },
        { value: 'fornuftig_pris', label: 'At prisen holdes på et fornuftigt niveau' },
        { value: 'tryg_valg', label: 'At jeg føler mig tryg i valget' }
      ]
    },
    {
      id: 'q2',
      question: 'Når vi gennemgår muligheder, foretrækker du…',
      type: 'radio',
      options: [
        { value: 'kort_overblik', label: 'Et kort overblik med få forslag' },
        { value: 'grundig_gennemgang', label: 'En mere grundig gennemgang med flere muligheder' }
      ]
    },
    {
      id: 'q3',
      question: 'Hvad giver dig mest ro i maven, når du skal vælge?',
      type: 'radio',
      options: [
        { value: 'klar_anbefaling', label: 'En klar anbefaling' },
        { value: 'par_muligheder', label: 'At kunne vælge mellem et par gode muligheder' },
        { value: 'alle_muligheder', label: 'At se alle muligheder, før jeg beslutter mig' }
      ]
    },
    {
      id: 'q4',
      question: 'Hvordan plejer du at træffe beslutninger?',
      type: 'radio',
      options: [
        { value: 'samme_dag', label: 'Ofte samme dag' },
        { value: 'betaenkningstid', label: 'Efter lidt betænkningstid' },
        { value: 'sammen_med_anden', label: 'Sammen med en anden' }
      ]
    },
    {
      id: 'q5',
      question: 'Hvad vil du helst undgå på besøget?',
      type: 'radio',
      options: [
        { value: 'for_lang_tid', label: 'At det tager for lang tid' },
        { value: 'foele_presset', label: 'At føle mig presset' },
        { value: 'forvirret_valg', label: 'At blive forvirret af for mange valg' },
        { value: 'mangle_overblik', label: 'At mangle overblik over pris og proces' }
      ]
    },
    {
      id: 'q6',
      question: 'Har du allerede en budgetramme i tankerne?',
      type: 'radio',
      options: [
        { value: 'ja', label: 'Ja' },
        { value: 'nej', label: 'Nej' }
      ]
    },
    {
      id: 'q6_followup',
      question: 'Vil du dele den med os? (valgfrit)',
      type: 'textarea',
      placeholder: 'F.eks. ca. beløb eller interval...',
      conditional: { question: 'q6', value: 'ja' },
      optional: true
    },
    {
      id: 'q7',
      question: 'Er der noget, vi skal være særligt opmærksomme på i dit hjem?',
      type: 'textarea',
      placeholder: 'F.eks. kæledyr, allergier, særlige ønsker...',
      optional: true
    }
  ];
}

function RadioOptions({ options, selectedValue, onSelect }) {
  const items = [];
  for (let i = 0; i < options.length; i++) {
    const option = options[i];
    const isSelected = selectedValue === option.value;
    items.push(
      <label
        key={option.value}
        className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
          isSelected
            ? 'border-teal-500 bg-teal-50'
            : 'border-slate-200 hover:border-slate-300 bg-white'
        }`}
        data-testid={`option-${option.value}`}
      >
        <RadioGroupItem value={option.value} className="sr-only" />
        <span
          className={`text-base ${
            isSelected ? 'text-teal-900 font-medium' : 'text-slate-700'
          }`}
        >
          {option.label}
        </span>
      </label>
    );
  }
  return (
    <RadioGroup value={selectedValue || ''} onValueChange={onSelect} className="space-y-2">
      {items}
    </RadioGroup>
  );
}

const SurveyPage = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [surveyData, setSurveyData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [visibleQuestions, setVisibleQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(-1); // -1 = intro
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  const [gdprConsentText, setGdprConsentText] = useState({
    heading: "Samtykke til behandling af personoplysninger",
    text: "Ved at sende denne formular giver jeg samtykke til, at Gardinbussen.org må behandle mine oplysninger til brug for mit kommende besøg. Data slettes automatisk efter 2 år.",
    checkbox_label: "Jeg accepterer behandling af mine personoplysninger"
  });
  const [landingText, setLandingText] = useState({
    heading: "Så vi kan give dig den bedste rådgivning",
    description: "Spørgsmålene hjælper os med at tilpasse besøget til dig, så vi undgår unødvendige valg og spildtid.",
    greeting: "Hej {fornavn}, det tager ca. 1 minut.",
    button_text: "Start de korte spørgsmål"
  });

  useEffect(() => {
    loadSurveyAndQuestions();
  }, [token]);

  useEffect(() => {
    // Update visible questions based on answers (for conditional logic)
    const visible = [];
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (q.conditional) {
        // Only show if condition is met
        if (answers[q.conditional.question] === q.conditional.value) {
          visible.push(q);
        }
      } else {
        visible.push(q);
      }
    }
    setVisibleQuestions(visible);
  }, [questions, answers]);

  const loadSurveyAndQuestions = async () => {
    try {
      // Load survey data
      const surveyResponse = await getSurvey(token);
      if (surveyResponse.data.submitted) {
        setSubmitted(true);
        setLoading(false);
        return;
      } else if (surveyResponse.data.expired) {
        setError('Link er udløbet');
        setLoading(false);
        return;
      }
      setSurveyData(surveyResponse.data);

      // Load questions from API
      try {
        const questionsResponse = await axios.get(`${API}/survey-questions`);
        setQuestions(questionsResponse.data.questions || getDefaultQuestions());
      } catch {
        setQuestions(getDefaultQuestions());
      }
      
      // Load landing page text
      try {
        const landingResponse = await axios.get(`${API}/survey-landing-text`);
        if (landingResponse.data.text) {
          setLandingText(landingResponse.data.text);
        }
      } catch {
        // Use defaults
      }
      
      // Load GDPR consent text
      try {
        const gdprResponse = await axios.get(`${API}/gdpr-consent-text`);
        if (gdprResponse.data.text) {
          setGdprConsentText(gdprResponse.data.text);
        }
      } catch {
        // Use defaults
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Link ikke fundet');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    if (currentStep < visibleQuestions.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > -1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStart = () => {
    setCurrentStep(0);
  };

  const handleSubmit = async () => {
    if (!gdprConsent) {
      setError('Du skal acceptere GDPR-samtykke for at indsende');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/survey/${token}/submit`, {
        answers,
        gdpr_consent: gdprConsent
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke gemme svar');
    } finally {
      setSubmitting(false);
    }
  };

  const currentQuestion = currentStep >= 0 ? visibleQuestions[currentStep] : null;
  const progress = currentStep >= 0 ? ((currentStep + 1) / visibleQuestions.length) * 100 : 0;
  const isLastQuestion = currentStep === visibleQuestions.length - 1;
  const canProceed = currentQuestion 
    ? (currentQuestion.type === 'textarea' || currentQuestion.optional || answers[currentQuestion.id])
    : true;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-slate-200">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Noget gik galt</h2>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-slate-200 shadow-lg">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-3 font-outfit">
              Tak – vi glæder os til at komme på besøg
            </h2>
            <p className="text-slate-500">
              Vi bruger dine svar til at forberede os, så besøget bliver så relevant og overskueligt som muligt.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Helper to replace placeholders
  const replacePlaceholders = (text) => {
    const firstName = surveyData?.customer_name?.split(' ')[0] || 'du';
    return text
      .replace(/{fornavn}/g, firstName)
      .replace(/{navn}/g, surveyData?.customer_name || 'du');
  };

  // Intro screen
  if (currentStep === -1) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-lg mx-auto px-4 py-12">
          <Card className="border-slate-200 shadow-lg">
            <CardContent className="p-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_72e2165b-e90b-4d70-9b27-bab6784408f0/artifacts/9iij87hr_Gardinbussen.org%20logo%20%281%29.png" 
                alt="Gardinbussen.org"
                className="h-12 mb-6 object-contain"
              />
              
              <h1 className="text-2xl font-bold text-slate-900 mb-3 font-outfit">
                {landingText.heading}
              </h1>
              
              <p className="text-slate-600 mb-8 leading-relaxed">
                {landingText.description}
              </p>
              
              {surveyData?.customer_name && (
                <p className="text-slate-500 text-sm mb-6">
                  {replacePlaceholders(landingText.greeting)}
                </p>
              )}
              
              <Button
                onClick={handleStart}
                className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-lg"
                data-testid="start-survey-btn"
              >
                {landingText.button_text}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4">
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-slate-400 mt-2 text-right">
            {currentStep + 1} af {visibleQuestions.length}
          </p>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8">
        <div className="min-h-[50vh] flex flex-col justify-center">
          <h2
            className="text-xl font-semibold text-slate-900 mb-6 font-outfit"
            data-testid={`question-${currentQuestion?.id}`}
          >
            {currentQuestion?.question}
          </h2>
          
          {currentQuestion?.optional && (
            <p className="text-sm text-slate-400 mb-4 -mt-4">(Valgfrit)</p>
          )}

          {currentQuestion?.type === 'radio' && (
            <RadioOptions
              options={currentQuestion.options || []}
              selectedValue={answers[currentQuestion.id]}
              onSelect={(value) => handleAnswer(currentQuestion.id, value)}
            />
          )}

          {currentQuestion?.type === 'textarea' && (
            <Textarea
              value={answers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
              placeholder={currentQuestion.placeholder}
              rows={4}
              className="text-base p-4"
              data-testid={`textarea-${currentQuestion.id}`}
            />
          )}
          
          {/* GDPR Consent - shown on last question */}
          {isLastQuestion && (
            <div className="mt-8 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-start gap-3 mb-3">
                <Shield className="h-5 w-5 text-slate-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-slate-900 text-sm">{gdprConsentText.heading}</h3>
                  <p className="text-xs text-slate-500 mt-1">{gdprConsentText.text}</p>
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={gdprConsent}
                  onCheckedChange={setGdprConsent}
                  data-testid="gdpr-consent-checkbox"
                />
                <span className="text-sm text-slate-700">{gdprConsentText.checkbox_label}</span>
              </label>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-8">
          <Button
            variant="outline"
            onClick={handleBack}
            className="flex-1 h-12"
            data-testid="back-btn"
          >
            Tilbage
          </Button>

          {isLastQuestion ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !gdprConsent}
              className="flex-1 h-12 bg-slate-900 hover:bg-slate-800"
              data-testid="submit-survey-btn"
            >
              {submitting ? (
                <span className="flex items-center">
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Sender...
                </span>
              ) : (
                'Send svar'
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!canProceed}
              className="flex-1 h-12 bg-slate-900 hover:bg-slate-800"
              data-testid="next-btn"
            >
              Næste
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default SurveyPage;
